/**
 * Who gets told when a diary entry is written (22 Sep 2026).
 *
 * Tom: "Can we send email notifications when we put in a new calendar
 * meeting? ... I want these to go to OWNERS with STAFF as an option."
 *
 * **24 Sep 2026: sending at all is now a choice.** It was unconditional, and
 * the reasoning was sound at the time — the calendar feature exists because a
 * meeting could go in without Zeena knowing, so a switch that turned the
 * owners off would put that back. The Week Ahead becoming the front page is
 * what changed it: a new entry is in front of both owners whether or not an
 * email goes, so the email can mean "this needs saying" rather than "this
 * exists". Tom, on a recording session that mailed everybody: "a tick box for
 * if something needs a notification".
 *
 * The order is now: send at all (off by default) → owners → everyone → guests.
 * Owners remain un-switchable WITHIN a notification: if it is worth an email
 * at all, it is worth both of them knowing.
 *
 * Guests are the one audience that leaves the building, so they are handled
 * differently everywhere: validated here AND in the database, capped, and
 * sent a plainer email with no Portal OS link in it.
 */

/** Loose on purpose — the real check is the database's, and an address only
    has to be plausible enough that a typo is caught before the send. */
const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const MAX_GUESTS = 10

/**
 * Split what was typed into addresses.
 *
 * Commas, semicolons and plain spaces all separate, because people paste
 * addresses out of Outlook and out of a WhatsApp message and the two do not
 * agree on a separator. Duplicates collapse — nobody wants the same person
 * emailed twice because they were pasted twice.
 */
export function parseGuestEmails(raw: string): string[] {
  const seen = new Set<string>()
  for (const piece of raw.split(/[,;\s]+/)) {
    const address = piece.trim().toLowerCase()
    if (address) seen.add(address)
  }
  return [...seen]
}

/** The first thing wrong with the guest list, in words, or '' if it is fine. */
export function guestEmailProblem(raw: string): string {
  const guests = parseGuestEmails(raw)
  const bad = guests.find((address) => !LOOKS_LIKE_EMAIL.test(address))
  if (bad) return `“${bad}” doesn’t look like an email address.`
  if (guests.length > MAX_GUESTS) {
    return `That’s ${guests.length} guests — ${MAX_GUESTS} is the limit for a diary entry.`
  }
  return ''
}

/** "The owners", "Everyone on the team and 2 guests" — the line under the
    switch, so what will happen is readable before it happens. Only ever shown
    when a notification is actually being sent. */
export function audienceSummary(notifyStaff: boolean, rawGuests: string): string {
  const guests = parseGuestEmails(rawGuests)
  const inside = notifyStaff ? 'Everyone on the team' : 'The owners'
  if (guests.length === 0) return `${inside} will get an email.`
  if (guests.length === 1) return `${inside}, and ${guests[0]}, will get an email.`
  return `${inside}, and ${guests.length} guests, will get an email.`
}
